import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { TFunction, useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as yup from "yup";

import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { PRIVATE_ROUTES } from "../constants";
import { useAppState } from "../context/StateContext";
import { Access } from "../models/access";
import { AutomaticSequencing } from "../models/automatic.sequencing";
import { Order, PreApprovalItem } from "../models/order";
import { Task } from "../models/task";
import OrderService from "../services/order.service";
import UserService from "../services/user.service";
import { Status, TaskStatus, UserTypes } from "../util/enums";
import useActionAutomaticSequence from "./useActionAutomaticSequence";
import { useOrderContext } from "./useOrderContext";
import { useTimezoneForJob } from "./useResolveTimezone";
import { getBrowserTimezoneName } from "Util/getBrowserTimezone";
import { useCallback } from "react";
import { formatOrderCustomerContact } from "./orderCustomerPayload";

interface SaveOrderProps {
  id?: number;
  order: Order;
}

type JobExtraInfoPayload = {
  reweighGrossWeight?: number | null;
  reweighTareWeight?: number | null;
  reweighNetWeight?: number | null;
  grade?: string | null;
  sitInDate?: string | null;
  sitOutDate?: string | null;
  sitNumber?: string | null;
};

type LocationExtraInfoPayload = {
  baseCode?: string | null;
  baseDescription?: string | null;
  areaCode?: string | null;
  areaDescription?: string | null;
  agentName?: string | null;
  agentZipcode?: string | null;
  agentCity?: string | null;
};

type ScacPayload = {
  description?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  claimPhoneNumber?: string | null;
  claimEmail?: string | null;
};

const toOptionalNumberOrNull = (v: unknown): number | null => {
  if (v === undefined || v === null || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const toOptionalStringOrNull = (v: unknown): string | null => {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s.length ? s : null;
};

const hasAnyNonNull = (obj: Record<string, unknown>) =>
  Object.values(obj).some((v) => v !== undefined && v !== null && v !== "");

const normalizePreApprovalNumber = (value: unknown): number | null => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

const preApprovalStringFilled = (v: unknown): boolean => {
  const s = typeof v === "string" ? v.trim() : v != null ? String(v).trim() : "";
  return s.length > 0;
};

const isPreApprovalItemComplete = (item: PreApprovalItem): boolean => {
  if (!preApprovalStringFilled(item?.description)) return false;
  return true;
};

/** SIT out must be strictly after SIT in (YYYY-MM-DD from date inputs). */
const isSitOutDateAfterSitInDate = (sitIn?: string | null, sitOut?: string | null): boolean => {
  const a = typeof sitIn === "string" ? sitIn.trim() : "";
  const b = typeof sitOut === "string" ? sitOut.trim() : "";
  if (!a || !b) return true;
  return b > a;
};

export const useActionOrder = () => {
  const { order, changedOptions, setGeneralErrors } = useOrderContext();
  const { currentUser, hasUnsavedChanges, setHasUnsavedChanges, userTimezone, agentCompany } =
    useAppState();
  const { isAutomaticSequenceReference, increaseAutomaticSequence } = useActionAutomaticSequence();

  const { getTimezone } = useTimezoneForJob();

  const { t: translate } = useTranslation();

  const queryClient = useQueryClient();

  const history = useHistory();

  const schema = yup.object().shape({
    pickupAccess: yup
      .object()
      .nullable()
      .test("pickupAccess", (access) => {
        const accessChanged = changedOptions?.includes("Access");

        if (accessChanged && access && !access.accessDetailsDescribed) return false;

        return true;
      }),
    deliveryAccess: yup
      .object()
      .nullable()
      .test("deliveryAccess", (access) => {
        const accessChanged = changedOptions?.includes("Access");

        if (accessChanged && access && !access.accessDetailsDescribed) return false;

        return true;
      }),
  });

  const validateTasks = (translate: TFunction<"translation", undefined>, order?: Order) => {
    if (!order) {
      NotificationToast.error(translate("addATaskHomeSurveyOrVideoSurveyToCreateAQuote"));

      return false;
    }

    return true;
  };

  const handleAccessValidation = (
    translate: TFunction<"translation", undefined>,
    accessPickup?: Access,
    accessDelivery?: Access
  ) => {
    try {
      schema.validateSync(
        {
          pickupAccess: accessPickup,
          deliveryAccess: accessDelivery,
        },
        {
          abortEarly: false,
        }
      );
      setGeneralErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setGeneralErrors(error.inner);
      NotificationToast.warning(translate("pleaseCheckTheAccessDetailsDescribedOnPickupAccess"));

      return false;
    }
  };

  const validateOrder = (translate: TFunction<"translation", undefined>, order: Order) => {
    if (!order.jobTypeId) {
      NotificationToast.warning(translate("pleaseSelectAOrderType"));

      return false;
    }

    if (!order.reference) {
      NotificationToast.warning(translate("pleaseFillAReference"));

      return false;
    }

    if (!order.origin?.customer || !order.origin?.customer.name || !order.origin.customer.email) {
      NotificationToast.warning(translate("pleaseFillPickUpCustomerFields"));

      return false;
    }

    if (!order.origin.locations || order.origin.locations.length <= 0) {
      NotificationToast.warning(translate("pleaseFillPickUpAddressFields"));

      return false;
    }

    if (
      !order.destination?.customer ||
      !order.destination?.customer.name ||
      !order.destination.customer.email
    ) {
      NotificationToast.warning(translate("pleaseFillDeliveryCustomerFields"));

      return false;
    }

    // Removing this validation
    // if (!order.destination?.customer?.phone && !order.destination?.customer?.destinationMobile) {
    //   NotificationToast.warning(translate("pleaseFillDeliveryCustomerFields"));

    //   return false;
    // }

    if (!order.destination.locations || order.destination.locations.length <= 0) {
      NotificationToast.warning(translate("pleaseFillDeliveryCustomerFields"));

      return false;
    }

    if (!order.destination.locations || order.destination.locations.length <= 0) {
      NotificationToast.warning(translate("pleaseFillDeliveryAddressFields"));

      return false;
    }

    for (const location of order.origin.locations) {
      if (location?.id && !String(location.id).includes("delete") && !location.longAddress) {
        NotificationToast.warning(translate("pleaseFillPickUpAddressFields"));

        return false;
      }
    }

    for (const location of order.destination.locations) {
      if (location?.id && !String(location.id).includes("delete") && !location.longAddress) {
        NotificationToast.warning(translate("pleaseFillDeliveryAddressFields"));

        return false;
      }
    }

    return true;
  };

  const validatePreApprovals = (
    translate: TFunction<"translation", undefined>,
    orderToValidate: Order
  ) => {
    const isMilitary = Boolean(
      orderToValidate?.company?.isMilitary ?? currentUser?.company?.isMilitary
    );
    if (!isMilitary) return true;

    const items = orderToValidate?.preApprovals ?? [];
    if (items.length === 0) return true;

    for (const item of items) {
      if (!isPreApprovalItemComplete(item)) {
        NotificationToast.warning(translate("pleaseFillAllPreApprovalFields"));
        return false;
      }
    }
    return true;
  };

  const validateSitDates = (
    translate: TFunction<"translation", undefined>,
    orderToValidate: Order
  ) => {
    const isMilitary = Boolean(
      orderToValidate?.company?.isMilitary ?? currentUser?.company?.isMilitary
    );
    if (!isMilitary) return true;
    if (!isSitOutDateAfterSitInDate(orderToValidate?.sitInDate, orderToValidate?.sitOutDate)) {
      NotificationToast.warning(translate("sitOutDateMustBeAfterSitInDate"));
      return false;
    }
    return true;
  };

  const publishTasks = (tasks: Task[]) => {
    const tasksUpdated: Task[] = [];

    if (tasks && tasks?.length > 0) {
      tasks?.forEach((task) => {
        if (task.status === TaskStatus.WAITING_PUBLISH) {
          tasksUpdated.push({
            ...task,
            status: TaskStatus.PENDING_ACCEPT,
            sendPushNotification: true,
          });
        } else {
          tasksUpdated.push(task);
        }
      });
    }

    return tasksUpdated;
  };

  const sendOrderAndPublish = async (
    callback?: (quoteId?: number) => void,
    sequence?: AutomaticSequencing
  ) => {
    if (
      order &&
      validateTasks(translate, order) &&
      validateOrder(translate, order) &&
      validatePreApprovals(translate, order) &&
      validateSitDates(translate, order) &&
      handleAccessValidation(translate, order?.pickupAccess, order?.deliveryAccess)
    ) {
      const orderUpdated = {
        ...order,
        tasks: order?.tasks && order?.tasks?.length > 0 ? publishTasks(order?.tasks) : [],
        timezone: userTimezone?.name,
        origin: {
          ...order.origin,
          customer: formatOrderCustomerContact(order.origin?.customer),
        },
        destination: {
          ...order.destination,
          customer: formatOrderCustomerContact(order.destination?.customer),
        },
      } as Order;

      setHasUnsavedChanges(false);
      mutate(
        { id: order?.id, order: orderUpdated },
        {
          onSuccess: ({ data }) => {
            NotificationToast.success(translate("operationPerformedSuccessfully"));
            if (
              sequence &&
              !order.id &&
              order.reference &&
              sequence.id &&
              isAutomaticSequenceReference(sequence, order.reference)
            )
              increaseAutomaticSequence({
                sequence,
                value: order.reference,
              });
            if (callback && data) {
              callback(data.id);
            } else {
              history.push(PRIVATE_ROUTES.ORDERS);
            }
          },
        }
      );
    }
  };

  const { mutate, isLoading: isUpdatingOrder } = useMutation(
    ["order.save"],
    ({ id, order }: SaveOrderProps) => {
      const isMilitary = Boolean(order?.company?.isMilitary ?? currentUser?.company?.isMilitary);

      const jobExtraInfo: JobExtraInfoPayload = {
        reweighGrossWeight: toOptionalNumberOrNull(order?.grossWeight),
        reweighTareWeight: toOptionalNumberOrNull(order?.tareWeight),
        reweighNetWeight: toOptionalNumberOrNull(order?.netWeight),
        grade: toOptionalStringOrNull(order?.grade),
        sitInDate: toOptionalStringOrNull(order?.sitInDate),
        sitOutDate: toOptionalStringOrNull(order?.sitOutDate),
        sitNumber: toOptionalStringOrNull(order?.sitNumber),
      };

      const originLocationExtraInfo: LocationExtraInfoPayload = {
        baseCode: toOptionalStringOrNull((order as any)?.pickupBaseCode),
        baseDescription: toOptionalStringOrNull((order as any)?.pickupBaseDescription),
        areaCode: toOptionalStringOrNull((order as any)?.pickupAreaCode),
        areaDescription: toOptionalStringOrNull((order as any)?.pickupAreaDescription),
        agentName: toOptionalStringOrNull((order as any)?.pickupAgentName),
        agentZipcode: toOptionalStringOrNull((order as any)?.pickupAgentZip),
        agentCity: toOptionalStringOrNull((order as any)?.pickupAgentCity),
      };

      const destinationLocationExtraInfo: LocationExtraInfoPayload = {
        baseCode: toOptionalStringOrNull((order as any)?.deliveryBaseCode),
        baseDescription: toOptionalStringOrNull((order as any)?.deliveryBaseDescription),
        areaCode: toOptionalStringOrNull((order as any)?.deliveryAreaCode),
        areaDescription: toOptionalStringOrNull((order as any)?.deliveryAreaDescription),
        agentName: toOptionalStringOrNull((order as any)?.deliveryAgentName),
        agentZipcode: toOptionalStringOrNull((order as any)?.deliveryAgentZip),
        agentCity: toOptionalStringOrNull((order as any)?.deliveryAgentCity),
      };

      const scac: ScacPayload = {
        description: toOptionalStringOrNull((order as any)?.scacDescription),
        phoneNumber: toOptionalStringOrNull((order as any)?.scacPhone),
        email: toOptionalStringOrNull((order as any)?.scacEmail),
        claimPhoneNumber: toOptionalStringOrNull((order as any)?.scacClaimsPhone),
        claimEmail: toOptionalStringOrNull((order as any)?.scacClaimsEmail),
      };

      const fallbackCurrencyCode =
        toOptionalStringOrNull((order as any)?.company?.currency?.code) ??
        toOptionalStringOrNull((currentUser as any)?.company?.currency?.code) ??
        "";

      const preApprovals = (order?.preApprovals ?? []).map((item) => {
        const { id: _preApprovalPersistedId, ...itemWithoutId } = item;
        return {
          ...itemWithoutId,
          goShareId: toOptionalStringOrNull(item?.goShareId) ?? "",
          code: toOptionalStringOrNull(item?.code) ?? "",
          description: toOptionalStringOrNull(item?.description) ?? "",
          unit: toOptionalStringOrNull(item?.unit) ?? "",
          currency: toOptionalStringOrNull(item?.currency) ?? fallbackCurrencyCode,
          count: normalizePreApprovalNumber(item?.count),
          rate: normalizePreApprovalNumber(item?.rate),
          total: normalizePreApprovalNumber(item?.total),
        };
      });

      const enhancedOrder = {
        ...order,
        preApprovals,
        isSalesApp: true,
        companyId: currentUser?.companyId,
        timezone: getTimezone(),
        ...(isMilitary
          ? {
              ...(hasAnyNonNull(jobExtraInfo) ? { jobExtraInfo } : {}),
              ...(hasAnyNonNull(originLocationExtraInfo)
                ? { originLocationExtraInfo }
                : {}),
              ...(hasAnyNonNull(destinationLocationExtraInfo)
                ? { destinationLocationExtraInfo }
                : {}),
              ...(hasAnyNonNull(scac) ? { scac } : {}),
            }
          : {}),
      };

      console.log("[useActionOrder] Saving order with timezone:", {
        resolvedTimezone: enhancedOrder.timezone,
        user: currentUser?.timezone?.name,
        browser: getBrowserTimezoneName(),
      });

      if (id) {
        return OrderService.update(Number(order.id), enhancedOrder, {
          actions: changedOptions,
        });
      }

      return OrderService.store(enhancedOrder);
    }
  );

  const sendOrder = useCallback(
    async (callback?: (orderId?: number) => void, sequence?: AutomaticSequencing) => {
      if (
        order &&
        validateTasks(translate, order) &&
        validateOrder(translate, order) &&
        validatePreApprovals(translate, order) &&
        validateSitDates(translate, order) &&
        handleAccessValidation(translate, order?.pickupAccess, order?.deliveryAccess)
      ) {
        setHasUnsavedChanges(false);

        mutate(
          {
            id: order?.id,
            order: {
              ...order,
              timezone: userTimezone?.name,
              origin: {
                ...order.origin,
                customer: formatOrderCustomerContact(order.origin?.customer),
              },
              destination: {
                ...order.destination,
                customer: formatOrderCustomerContact(order.destination?.customer),
              },
            },
          },
          {
            onSuccess: async ({ data }) => {
              NotificationToast.success(translate("operationPerformedSuccessfully"));

              await queryClient.invalidateQueries({ queryKey: ["order.list"] });
              await queryClient.invalidateQueries({ queryKey: ["order.count"] });

              if (
                sequence &&
                !order.id &&
                order.reference &&
                sequence.id &&
                isAutomaticSequenceReference(sequence, order.reference)
              ) {
                increaseAutomaticSequence({
                  sequence,
                  value: order.reference,
                });
              }

              if (callback && data) {
                callback(data.id);
              } else {
                history.push(PRIVATE_ROUTES.ORDERS);
              }
            },
          }
        );
      }
    },
    [
      order,
      translate,
      userTimezone?.name,
      setHasUnsavedChanges,
      mutate,
      queryClient,
      history,
      currentUser?.company?.isMilitary,
    ]
  );

  const { mutate: sendCustomerPortalLink, isLoading: isSendingCustomerPortalLink } = useMutation(
    ["order.send.customer.portal.link"],
    ({ jobId }: { jobId: number }) => OrderService.sendCustomerPortalLink({ jobId }),
    {
      onSuccess: () => NotificationToast.success(translate("operationPerformedSuccessfully")),
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;

        NotificationToast.warning(errorAxios?.response?.data?.message);
      },
    }
  );

  const getAllUsers = () =>
    useQuery({
      queryKey: ["users.getAll.in.operations", order?.branchFromId, order?.toBranchId],
      queryFn: async () => {
        const filterByBranchId: number[] = [];

        if (order?.branchFromId) filterByBranchId.push(order?.branchFromId);
        if (order?.toBranchId) filterByBranchId.push(order?.toBranchId);

        const response = await UserService.getAll({
          attributes: JSON.stringify(["id", "name", "lastName", "fullName", "email", "phone"]),
          include: JSON.stringify([
            {
              association: "usersCompanies",
              required: true,
              include: [
                {
                  association: "manager",
                  attributes: ["id", "name", "lastName", "fullName", "email", "phone"],
                },
              ],
              where: {
                status: Status.ACTIVE,
                typeUser: [UserTypes.ADMIN, UserTypes.SALES],
                companyId:
                  currentUser?.company.profileId === 6 ? agentCompany : currentUser?.companyId,
              },
            },
            {
              association: "branches",
              required: true,
              where: filterByBranchId?.length
                ? {
                    id: filterByBranchId,
                  }
                : null,
            },
          ]),
          sortBy: JSON.stringify(["name"]),
        });

        if (response && response?.data) {
          return response.data.map((data) => ({
            ...data,
            status: data.usersCompanies?.[0]?.status,
            manager: data.usersCompanies?.[0]?.manager,
            companyId: data.usersCompanies?.[0]?.companyId,
          }));
        }
      },

      enabled: !!currentUser?.companyId && (!!order?.branchFromId || !!order?.toBranchId),
    });

  return {
    sendOrder,
    sendOrderAndPublish,
    isUpdatingOrder,
    setHasUnsavedChanges,
    hasUnsavedChanges,
    sendCustomerPortalLink,
    isSendingCustomerPortalLink,
    getAllUsers,
  };
};
